import React, { useEffect, useState } from "react";
import Action from "../../components/common/Action";
import DebounceSearch from "../../components/common/DebounceSearch";
import CustomTable from "../../components/customtable/CustomTable";
import { useDispatch } from "react-redux";
import Breadcrumb from "../../components/common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useSelector } from "react-redux";
import {
  getAffirmationlist,
  deleteAffirmation,
} from "../../redux/affirmation/slice";
import { SortDownArrow } from "../../assets/svg";

const Affirmations = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [affirmationDelete, setAffirmationDelete] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);

  // selector
  const { affirmationList, affirmationListLoading } = useSelector((store) => ({
    affirmationList:
      store?.affirmationData?.getAffirmationlistStatus?.data?.data,
    affirmationListLoading: store?.affirmationData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getAffirmationlist({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      }),
    );
  }, [dispatch, searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  // Delete movement
  const deleteAffirmationId = () => {
    setIsLoading(true);
    if (!affirmationDelete) return;
    dispatch(deleteAffirmation(affirmationDelete)).then((res) => {
      setIsLoading(false);
      if (res.type === "deleteAffirmation/fulfilled") {
        dispatch(
          getAffirmationlist({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          }),
        );
      }
      setConfirmDelete(false);
    });
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="w-24 table-th">No.</th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Name</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" ? "rotate-180" : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="w-24 table-th">Actions</th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(affirmationList?.data || []).map((item, index) => (
        <tr key={item.id} className="shadow-sm text-mgrey hover:bg-navTheme/5">
          <td className="px-5 py-3">
            {(pageCount - 1) * pageLimit + index + 1}
          </td>
          <td className="px-5 py-3">
            <div className="line-clamp-1">{item?.name || "-"}</div>
          </td>
          <td className="px-5 py-3 text-start">
            <div className="flex justify-start">
              <Action
                optionList={
                  <>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() =>
                        navigate("/add-edit-affirmation", {
                          state: { affirmation: item, mode: "edit" },
                        })
                      }
                    >
                      View/Edit
                    </li>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() => {
                        setConfirmDelete(true);
                        setAffirmationDelete(item?._id);
                      }}
                    >
                      Delete
                    </li>
                  </>
                }
              />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div className="space-y-5">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            Affirmations
          </h6>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbActive={"Affirmations"}
            breadCrumbTitleKey={"/"}
            classes=""
          />
        </div>
        {/* Search and Export Section */}
        <div className="flex items-center space-x-4 w-full">
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <button
            className="bg-themeBtn text-white flex items-center justify-center px-2 py-[9px] rounded uppercase font-figtree text-base w-60 shadow-[0_4px_6px_rgba(0,0,0,0.3)]"
            onClick={() => navigate("/add-edit-affirmation")}
            type="button"
          >
            + Add Affirmation
          </button>
        </div>

        <div className="w-full bg-white shadow rounded-lg">
          <CustomTable
            overflowVisibleLg={true}
            columnHeaders={<ColumnHeaders />}
            dataRows={<DataRows />}
            data={affirmationList}
            loading={affirmationListLoading}
            showPagination={true}
            disableNext={!!affirmationList?.data?.length}
            disablePrev={!!affirmationList?.data?.length}
            setPaginationCurrentLimit={setPageLimit}
            paginationCurrentLimit={pageLimit}
            current={pageCount}
            total={affirmationList?.pagination?.total}
            paginationData={(crPage) => setPageCount(crPage)}
            lastPage={Math.ceil(affirmationList?.pagination?.total / pageLimit)}
          />
        </div>
      </div>
      {confirmDelete && (
        <div>
          <ConfirmationModel
            confirmText={"Delete"}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onConfirm={deleteAffirmationId}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default Affirmations;
